import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { DropzoneState } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import Button, { ButtonType } from 'hellospa/foundation/control/button';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import dragAndDropIcon from 'images/v2/modules/home/draganddrop_icon_@2x.png';
import dropboxIcon from 'images/v2/modules/home/integrations/dropbox_icon.svg';
import googleDriveIcon from 'images/v2/modules/home/integrations/drive_icon_@2x.png';
import boxIcon from 'images/v2/modules/home/integrations/box_icon_@2x.png';
import evernoteIcon from 'images/v2/modules/home/integrations/evernote_icon_@2x.png';
import oneDriveIcon from 'images/v2/modules/home/integrations/onedrive_icon_@2x.png';
import styles from '../styles.scss';

type Props = {
  allowUpload: boolean,
  allowTemplate: boolean,
  isBulkSend: boolean,
  allowBulkSend: boolean,
  allowIntegrations: boolean|undefined,
  onClickAddTemplateButton: () => void,
  toggleIntegrationPicker: (integration: string, status: boolean) => void,
  integrations: any,
  dropzone: DropzoneState,
}

const InlineLayout: React.FunctionComponent<Props> = ({
  allowUpload,
  allowTemplate,
  isBulkSend,
  allowBulkSend,
  allowIntegrations,
  onClickAddTemplateButton,
  toggleIntegrationPicker,
  integrations,
  dropzone: { getRootProps, getInputProps, open, isDragActive },
}) => {
  const shouldRouteToPrepAndSend = useSelector(selectors.shouldRouteToPrepAndSend);

  const addFileButton = allowUpload
    && (
      <span className={styles.uploadContainerSpan}>
        <input {...getInputProps()} data-qa-ref="upload-input" />
        <Button
          data-qa-ref="prep-upload-files"
          kind={ButtonType.Outline}
          onClick={open}
        >
          <FormattedMessage
            id=""
            description="button text for prep and send to upload files"
            defaultMessage="Upload file" />
        </Button>
      </span>
    );

  const dragAndDropContainer = allowUpload
    && <div className={classNames(
      styles.dragAndDropContainer,
      {
        [styles.dragActive]: isDragActive,
        [styles.dragAndDropContainerPadding]: shouldRouteToPrepAndSend,
      },
    )}
      data-qa-ref="prep-drag-drop-container"
      // allow the drag & drop container to trigger the file picker
      onClick={open}
    >
      <img src={dragAndDropIcon} />
      <div className={styles.dragAndDropText}>
        <FormattedMessage
          id=""
          description="text for prep and send to drag and drop files"
          defaultMessage="Drag and drop files here" />
      </div>
    </div>;

  const addTemplateButton = allowTemplate
    && <Button
      data-qa-ref="prep-add-template"
      role="button"
      disabled={isBulkSend && !allowBulkSend}
      kind={ButtonType.Outline}
      onClick={onClickAddTemplateButton}
    >
      <FormattedMessage
        id=""
        description="button text for prep and send to choose template"
        defaultMessage="Add template" />
    </Button>;

  const uploadContainerClassName = classNames(
    styles.uploadContainer,
    {
      [styles.uploadContainerBorder]: allowUpload,
    },
  );

  const buttonsContainerClassName = classNames(
    styles.buttonsContainer,
    {
      [styles.noPadding]: !allowUpload && allowTemplate,
    },
  );

  return (
    <div
      {...getRootProps()}
      data-qa-ref="upload-container"
      className={uploadContainerClassName}
    >
      <div className={buttonsContainerClassName}>
        {addFileButton}
        {!shouldRouteToPrepAndSend && addTemplateButton}
      </div>
      {!shouldRouteToPrepAndSend && allowIntegrations && (
        <div className={styles.integrationsContainer}>
          <div className={styles.integrationsText}>
            <FormattedMessage
              id=""
              description="text for prep and send for upload from integration"
              defaultMessage="Upload from" />
          </div>
          <div className={styles.integrationsIcons}>
            {integrations && integrations[types.UploadIntegrationType.Dropbox] && (
              <div>
                <span
                  data-qa-ref="prep-dropbox-integration"
                  onClick={() => {
                    toggleIntegrationPicker(types.UploadIntegrationType.Dropbox, true);
                  }}
                >
                  <img src={dropboxIcon} />
                </span>
              </div>
            )}
            {integrations && integrations[types.UploadIntegrationType.GoogleDrive] && (
              <div>
                <span
                  data-qa-ref="prep-google-drive-integration"
                  onClick={() => {
                    toggleIntegrationPicker(types.UploadIntegrationType.GoogleDrive, true);
                  }}
                >
                  <img src={googleDriveIcon} />
                </span>
              </div>
            )}
            {integrations && integrations[types.UploadIntegrationType.Box] && (
              <div>
                <span
                  data-qa-ref="prep-box-integration"
                  onClick={() => toggleIntegrationPicker(types.UploadIntegrationType.Box, true)}
                >
                  <img src={boxIcon} />
                </span>
              </div>
            )}
            {integrations && integrations[types.UploadIntegrationType.Evernote] && (
              <div>
                <span
                  data-qa-ref="prep-evernote-integration"
                  onClick={() => {
                    toggleIntegrationPicker(types.UploadIntegrationType.Evernote, true);
                  }}
                >
                  <img src={evernoteIcon} />
                </span>
              </div>
            )}
            {integrations && integrations[types.UploadIntegrationType.OneDrive] && (
              <div>
                <span
                  data-qa-ref="prep-one-drive-integration"
                  onClick={() => {
                    toggleIntegrationPicker(types.UploadIntegrationType.OneDrive, true);
                  }}
                >
                  <img src={oneDriveIcon} />
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {dragAndDropContainer}
    </div>
  );
};

export default InlineLayout;
